import { createApi } from "@reduxjs/toolkit/query/react";

import type {
  IGetBranchByIdRequest,
  TCreateBranchResponse,
  TGetBranchByIdResponse,
  TGetBranchesRequest,
  TGetBranchesResponse,
  TUpdateBranchResponse,
} from "@/types/(company)/branches.interface";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";

export const companyBranchesApi = createApi({
  reducerPath: "companyBranchesApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["getBranches"],
  endpoints: (builder) => ({
    getBranches: builder.query<TGetBranchesResponse, TGetBranchesRequest | void>({
      query: ({ page = 1, limit = 100, search } = {}) => ({
        url: "/company/branches",
        method: "GET",
        params: {
          page,
          limit,
          ...(search ? { search } : {}),
        },
      }),
      providesTags: ["getBranches"],
    }),
    createBranch: builder.mutation<TCreateBranchResponse, FormData>({
      query: (body) => {
        return {
          url: "/company/branches",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["getBranches"],
    }),
    updateBranch: builder.mutation<
      TUpdateBranchResponse,
      { id: number; body: FormData }
    >({
      query: ({ id, body }) => ({
        url: `/company/branches/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _err, { id }) => [
        "getBranches",
        { type: "getBranches", id },
      ],
    }),
    getBranchById: builder.query<TGetBranchByIdResponse, IGetBranchByIdRequest>(
      {
        query: (id) => ({
          url: `/company/branches/${id}`,
          method: "GET",
        }),
        providesTags: (_result, _err, id) => [{ type: "getBranches", id }],
      },
    ),
    deleteBranch: builder.mutation<
      { success: boolean; message: string },
      number
    >({
      query: (id) => ({
        url: `/company/branches/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["getBranches"],
    }),
    getAllBranch: builder.infiniteQuery<
      TGetBranchesResponse,
      { limit: number },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (result) => {
          if (!result?.meta) return undefined;
          const { page, lastPage } = result.meta;
          return page < lastPage ? page + 1 : undefined;
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: "/company/branches",
        method: "GET",
        params: {
          limit: queryArg.limit,
          page: pageParam,
        },
      }),
    }),
  }),
});

export const {
  useGetBranchesQuery,
  useGetBranchByIdQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useDeleteBranchMutation,
  useGetAllBranchInfiniteQuery,
} = companyBranchesApi;
